'use client';

import { c as _c } from "react/compiler-runtime";
import { useSignedMutation } from '@achra/sdk/react';
import { useQueryClient } from '@tanstack/react-query';
import type { RsBillingCycle, ServiceOfferingFieldsFragment } from '@/modules/__generated__/graphql/switchboard-generated';
import type { CreateResourceInstancesResult, PurchaseOptionGroup } from '@/modules/service-purchase/types';
import { useTeamAdminDrive } from '@/modules/shared/hooks/use-team-admin-drive';
import { driveLinkFor } from '@/modules/shared/lib/switchboard-urls';
import type { ServiceOfferingState } from '@powerhousedao/op-hub/document-models/service-offering';
import type { BillingCycle as SIBillingCycle } from '@powerhousedao/op-hub/document-models/subscription-instance';
export interface SubmitPurchaseRequestInput {
  name: string;
  teamName: string;
  email: string;
  service: ServiceOfferingFieldsFragment;
  selectedTierId: string;
  selectedBillingCycle: RsBillingCycle;
  optionGroups: PurchaseOptionGroup[];
}
export function useSubmitPurchaseRequest() {
  const $ = _c(7);
  const queryClient = useQueryClient();
  const {
    teamAdminDrive
  } = useTeamAdminDrive();
  let t0;
  if ($[0] !== teamAdminDrive) {
    t0 = async (input, t1) => {
      const {
        signer,
        address,
        client
      } = t1;
      const {
        driveSlug
      } = await client.purchases.create({
        offering: input.service as unknown as ServiceOfferingState,
        tierId: input.selectedTierId,
        billingCycle: input.selectedBillingCycle as unknown as SIBillingCycle,
        optionGroupIds: input.optionGroups.filter(_temp).map(_temp2),
        customer: {
          name: input.name,
          teamName: input.teamName,
          email: input.email
        },
        signer,
        address,
        existingBuilderDrive: teamAdminDrive ? {
          driveId: teamAdminDrive.driveId,
          builderProfileId: teamAdminDrive.builderProfileId ?? undefined,
          driveSlug: teamAdminDrive.driveSlug
        } : undefined
      });
      return {
        name: input.name,
        teamName: input.teamName,
        email: input.email,
        driveUrl: driveLinkFor(driveSlug)
      };
    };
    $[0] = teamAdminDrive;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== queryClient) {
    t1 = () => {
      queryClient.invalidateQueries({
        queryKey: ["GetBuilderDrives"]
      });
      queryClient.invalidateQueries({
        queryKey: ["GetBuilderDrivesSuspense"]
      });
    };
    $[2] = queryClient;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t0 || $[5] !== t1) {
    t2 = {
      mutationFn: t0,
      onSuccess: t1
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return useSignedMutation(t2);
}
function _temp2(g_0) {
  return g_0.id;
}
function _temp(g) {
  return g.isSelected && g.services.length > 0;
}